import React, { useContext, useState } from 'react';
import { View, Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/native';

import config from 'app/src/config';
import LANGUAGES from 'app/src/config/languages';
import ClientContext from 'app/src/contexts/ClientContext';
import { Link } from 'app/src/utils/routing';
import ExternalLink from 'app/src/elements/ExternalLink';
import { Text } from 'app/src/styles';

export const Container = styled(View)`
  flex-direction: row;
  background-color: black;
  padding: 10px;
  justify-content: space-between;
  height: 40px;
  z-index: 999;
`;

export const Items = styled(View)`
  flex-direction: row;
  gap: ${props => props.isTablet ? '14px' : '20px'};
  align-items: center;
`;

export const Label = styled(Text)`
  font-size: ${props => props.isTablet ? '16px' : '18px'};
  font-weight: 600;
  color: white;
`;

const Account = styled(View)`
  position: absolute;
  right: 0px;
  top: 30px;
  background-color: black;
  padding: 20px;
  width: 200px;
  gap: 20px;
`;

export const DEALER_ITEMS = [
  {
    path: '/tags/register',
    label: 'Pre-Register Sale',
  },
  {
    path: '/pre-registrations',
    label: 'Pre-Registration History',
    tablet: 'History',
  },
  {
    path: '/tags',
    label: 'Tag Log',
  },
  {
    path: '/orders',
    label: 'Past Orders',
  },
  {
    url: 'https://autodealersresource.com/collections/temporary-tags',
    label: 'Order Tags',
  },
  {
    path: '/dealers',
    label: 'Dealerships',
  },
  {
    path: 'how-to',
    label: 'How To & FAQs',
    tablet: 'How To',
  },
];

export const EMPLOYEE_ITEMS = [
  {
    path: '/',
    label: 'Home',
  },
  {
    path: '/dealers',
    label: 'Dealers',
  },
  {
    path: '/dealers/new',
    label: 'Add Dealer',
    tablet: '+Dealer',
  },
  {
    path: '/orders',
    label: 'Orders',
  },
  {
    path: '/orders/new',
    label: 'New Order',
    tablet: '+Order',
  },
  {
    path: 'tags/register',
    label: 'Register Tag',
    tablet: '+Tag',
  },
  {
    path: 'how-to',
    label: 'How To & FAQs',
    tablet: 'How To',
  },
];

export const ADMIN_ITEMS = [
  {
    path: '/dealers/approval-status/pending',
    label: 'Pending Dealers',
    tablet: 'Pending',
  },
  {
    path: '/exports',
    label: 'Export Data',
    tablet: 'Export',
  },
  {
    path: '/financial',
    label: 'Financial',
  },
  {
    path: '/makes',
    label: 'Makes',
  },
];

export const AGENCY_ITEMS = [
  {
    path: '/',
    label: 'Home',
  },
  {
    path: '/dealers',
    label: 'Dealers',
  },
  {
    path: '/exports',
    label: 'Export Data',
    tablet: 'Export',
  },
  {
    path: 'how-to',
    label: 'How To & FAQs',
    tablet: 'How To',
  },
];

export const ACCOUNT_ITEMS = [
  {
    path: '/account/profile',
    label: 'Profile',
  },
  {
    path: '/account/password',
    label: 'Change Password',
  },
  {
    path: '/account/two-factor',
    label: 'Two-Factor Auth',
  },
  {
    path: '/users',
    label: 'Users',
  },
  {
    path: '/feature-subscriptions',
    label: 'Subscriptions',
  },
  {
    path: '/access-tokens',
    label: 'API Access Tokens',
  },
  {
    action: 'logout',
    label: 'Logout',
  },
];

const Menu = () => {
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const { i18n } = useTranslation();
  const {
    currentUser, activeDealers, isTablet, logout,
  } = useContext(ClientContext);

  let mainItems = [];
  if ('dealer' === currentUser?.role) {
    if (0 === activeDealers.length) {
      mainItems = [];
    } else {
      mainItems = DEALER_ITEMS;
    }
  } else if ('admin' === currentUser?.role) {
    mainItems = [...EMPLOYEE_ITEMS, ...ADMIN_ITEMS];
  } else if ('agency' === currentUser?.role) {
    mainItems = AGENCY_ITEMS;
  } else if (currentUser) {
    mainItems = EMPLOYEE_ITEMS;
  }

  return (
    <Container>
      <Items isTablet={isTablet}>
        {mainItems.map(({ label, path, url, tablet }) => {
          if (url) {
            return (
              <ExternalLink key={label} url={url}>
                <Label>{label}</Label>
              </ExternalLink>
            );
          } else {
            return (
              <Link key={label} to={path}>
                <Label isTablet={isTablet}>
                  {(isTablet && tablet) ? tablet : label}
                </Label>
              </Link>
            );
          }
        })}
      </Items>

      {config.app.selectLanguage && (
        <Items>
          {Object.keys(LANGUAGES).map(languageCode => (
            <Pressable
              key={languageCode}
              onPress={() => i18n.changeLanguage(languageCode)}
            >
              <Label isTablet={isTablet}>
                {languageCode.toUpperCase()}
              </Label>
            </Pressable>
          ))}
        </Items>
      )}

      <Items>
        <Pressable onPress={() => setShowAccountMenu(!showAccountMenu)}>
          <Label isTablet={isTablet}>
            {!isTablet && `${currentUser.fullName} - `}Account
          </Label>

          {showAccountMenu && (
            <Account>
              {ACCOUNT_ITEMS.map(({ label, path, action }) => {
                if ('logout' === action) {
                  return (
                    <Pressable key={label} onPress={logout}>
                      <Label>Logout</Label>
                    </Pressable>
                  );
                } else {
                  return (
                    <Link key={label} to={path}>
                      <Label>{label}</Label>
                    </Link>
                  );
                }
              })}
            </Account>
          )}
        </Pressable>
      </Items>
    </Container>
  );
};

export default Menu;
